import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { C } from '@parsina/framework/clinical/src/constants/theme';

/**
 * Server-side pagination control, shared by Accounting and Encounters.
 * `page` is 1-based. Renders nothing when there is a single page or no rows.
 * RTL: «قبلی» sits on the right (leading edge), «بعدی» on the left.
 *
 * Props:
 *   page, pageCount   — 1-based current page and total pages
 *   onPage            — (nextPage) => void
 *   total, pageSize   — optional; enables the "نمایش from–to از total" label
 */
export default function Pager({ page, pageCount, onPage, total, pageSize }) {
  const { t, formatNumber } = useTranslation();
  if (!pageCount || pageCount <= 1) return null;

  const canPrev = page > 1;
  const canNext = page < pageCount;

  const from = total != null && pageSize ? (page - 1) * pageSize + 1 : null;
  const to   = total != null && pageSize ? Math.min(page * pageSize, total) : null;

  const btn = (enabled) => ({
    padding: '7px 16px',
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: '#fff',
    color: enabled ? C.textPrimary : C.textMuted,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.5,
    fontFamily: 'inherit',
    fontSize: '0.8125rem',
    fontWeight: 600,
  });

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 14, padding: '16px 0', flexWrap: 'wrap',
    }}>
      <button style={btn(canPrev)} disabled={!canPrev} onClick={() => canPrev && onPage(page - 1)}>
        {t('common.pagination.prev')}
      </button>
      <span style={{ fontSize: '0.8125rem', color: C.textSecondary, fontWeight: 600 }}>
        {t('common.pagination.page')} {formatNumber(page)} {t('common.labels.from')} {formatNumber(pageCount)}
      </span>
      <button style={btn(canNext)} disabled={!canNext} onClick={() => canNext && onPage(page + 1)}>
        {t('common.pagination.next')}
      </button>
      {from != null && (
        <span style={{ fontSize: '0.75rem', color: C.textMuted }}>
          {t('common.pagination.showing')} {formatNumber(from)}–{formatNumber(to)} {t('common.labels.from')} {formatNumber(total)}
        </span>
      )}
    </div>
  );
}
